import type { RxJsonSchema } from 'rxdb'

/**
 * CustomFieldTab Document
 *
 * Workspace-level tab that hosts custom fields on the task detail view.
 * Two system tabs (Overview, Properties) are seeded per workspace by the backend
 * and are protected from delete and from render-mode / scope changes.
 *
 * Scoping (workItemTypeIds, projectIds) intentionally differs from CustomFieldDefinition:
 * empty arrays mean UNRESTRICTED — opposite of field scoping (where empty = hidden).
 * Populated arrays narrow the tab to those WITs / projects with AND logic.
 */
export interface CustomFieldTabDocument {
  id: string
  envId: string
  name: string

  /** Lucide icon name (e.g. "Settings", "Layers", "Info"). */
  icon: string

  /** "tab" appears in the task detail tab strip. "inline" renders compactly in the sidebar. */
  renderMode: 'tab' | 'inline'

  /** True for the seeded Overview / Properties tabs. Server-controlled. */
  isSystem: boolean

  sortOrder?: string | null

  /** Denormalized from backend junction tables. Empty = unrestricted. */
  workItemTypeIds: Array<string>
  projectIds: Array<string>

  isDeleted: boolean

  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null
  updatedAt?: number
}

export const customFieldTabSchema: RxJsonSchema<CustomFieldTabDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 36 },
    envId: { type: 'string', maxLength: 36 },
    name: { type: 'string', maxLength: 100 },
    icon: { type: 'string', maxLength: 64 },
    renderMode: { type: 'string', enum: ['tab', 'inline'], maxLength: 10 },
    isSystem: { type: 'boolean', default: false },
    sortOrder: { type: ['string', 'null'], maxLength: 256 },
    workItemTypeIds: {
      type: 'array',
      items: { type: 'string', maxLength: 36 },
      default: [],
    },
    projectIds: {
      type: 'array',
      items: { type: 'string', maxLength: 36 },
      default: [],
    },
    isDeleted: { type: 'boolean', default: false },
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: { type: 'string', maxLength: 36 },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: { type: 'string', maxLength: 36 },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: { type: ['string', 'null'], maxLength: 36 },
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'name',
    'icon',
    'renderMode',
    'isSystem',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [['envId', 'isDeleted'], 'updatedOn'],
}

export const customFieldTabMigrationStrategies = {}
